// React
import { useState } from "react";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid, Stack } from "@mui/material";

// Third party
import actionsList from "@Constants/actionsList";

// API
import { payRateIngestion, getPayRate, getWorkedHours, requestWorkedHoursIngestion } from "@DataPages/api/timeAndPayRates";

// Hooks/Contexts
import { RootState } from "@/redux/store";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import PayRatesContent from "./PayRatesContent";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import {
	IDepartmentPosition,
	IPayPeriodPayRate,
	IPayPeriodWordedHours,
	IPayRate,
	IUserWorkedHours,
} from "@/DataPages/types/timeAndPayRates/models";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Helpers
import { getPositionDepartment } from "@/Common/helpers/Positions";
import { Nullable } from "@/Common/interfaces/types";
import { or } from "@/Common/helpers/sonar";
import { getLocalDate } from "@/Common/helpers/Dates";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const getLastUpdatedDate = (date: string | undefined, timezone: string | undefined, dateFormat = DATE_FORMAT.usDateTime24hSeconds) => {
	if (!date) {
		return null;
	}
	return getLocalDate(date, dateFormat, timezone) ?? "";
};

const getEmptyTitle = (selected: IPeriodTreeSelection, isAccessGranted: boolean) => {
	if (!isAccessGranted) {
		return "You do not have access to view Work Hours and Pay Rates";
	}
	if (!selected) {
		return "Select a month to view the Work Hours and Pay Rates";
	}
	return "No Work Hours and Pay Rates found for this month";
};

const matchDepartments = (positions: Nullable<string[]> = [], workedHours: IUserWorkedHours[] = [], payRates: IPayRate[] = []) => {
	const departments = new Map();

	if (!positions?.length) {
		return [];
	}

	const filteredPayRates = payRates
		.map((record) => (record?.user && positions.includes(record.user.position) ? record : null))
		.filter((record) => record !== null);

	const filteredWorkedHours = workedHours
		.map((record) => (record?.user && positions.includes(record.user.position) ? record : null))
		.filter((record) => record !== null);

	if (filteredWorkedHours.length || filteredPayRates.length) {
		const information = mergeUsersRecords(filteredWorkedHours, filteredPayRates);

		positions.forEach((position) => {
			const department = getPositionDepartment(position);
			const stored = departments.get(department) || { department, listPositions: [] };
			const records = getPositionRecords(position, information);
			if (records?.length) {
				stored.listPositions.push({
					position,
					records,
				});

				departments.set(department, stored);
			}
		});
	}

	return Array.from(departments.values());
};

const mergeUsersRecords = (usersWorkedHours: IUserWorkedHours[] = [], usersPayRates: IPayRate[] = []) => {
	const usersMap = new Map();

	const updateUser = (element: IUserWorkedHours | IPayRate, key: string, value: number | number[]) => {
		const user = usersMap.get(element.user._id) || {
			_id: element.user._id,
			fullName: element.user.fullName,
			position: element.user.position,
			rate: 0,
			hours: 0,
			secondaries: [],
		};

		user[key] = value || Array.isArray(value) ? value : 0;
		usersMap.set(element.user._id, user);
	};

	usersPayRates.forEach((element) => updateUser(element, "rate", element.rate));
	usersPayRates.forEach((element) => updateUser(element, "secondaries", element.secondaries ?? []));
	usersWorkedHours.forEach((element) => updateUser(element, "hours", element.hours));

	return Array.from(usersMap.values());
};

const getPositionRecords = (pos: string, records: IDepartmentPosition[] = []) => {
	const results = records.filter((record) => record.position === pos);

	return results.map((result) => {
		const { position, ...rest } = result;
		return rest;
	});
};

interface IPayRatesBodyProps {
	selected: IPeriodTreeSelection;
	managedPositions: Nullable<string[]>;
	isLoadingPositions: boolean;
	isUpdatable: boolean;
	isAccessGranted: boolean;
}

const PayRatesBody = (props: IPayRatesBodyProps) => {
	const { selected, managedPositions, isLoadingPositions, isUpdatable, isAccessGranted } = props;

	const [ingesting, setIngesting] = useState(false);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	const queryClient = useQueryClient();

	// Permissions
	const { useAuth } = useAuthorization();
	const SYNC_PAY_RATES = useAuth(actionsList.SYNC_PAY_RATES);
	const SYNC_TIME_AND_ATTENDANCE = useAuth(actionsList.SYNC_TIME_AND_ATTENDANCE);

	// Subscription
	const { getSubscription } = useSubscriptions();
	const payRatesSubscription = getSubscription(["timeAndPayRates", selected?.payPeriodId]);
	payRatesSubscription?.setInvalidate([
		["workedHours", locationId, selected?.cycleId, selected?.payPeriodId],
		["payRates", locationId, selected?.cycleId, selected?.payPeriodId],
	]);

	payRatesSubscription?.subscriptions?.onCreatePayRate?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["payRates", locationId, selected?.cycleId, selected?.payPeriodId],
			exact: true,
			refetchType: "all",
		});
		setIngesting(false);
	});

	payRatesSubscription?.subscriptions?.onUpdateWorkedHours?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateWorkedHours", IPayPeriodWordedHours>) => {
			const updatedWorkedHours = result.data?.onUpdateWorkedHours;

			if (!updatedWorkedHours) {
				return;
			}
			queryClient.setQueryData(["workedHours", locationId, selected?.cycleId, selected?.payPeriodId], () => updatedWorkedHours);
			setIngesting(false);
		}
	);

	const { data: payRates, isLoading: isLoadingPayRates } = useQuery({
		queryKey: ["payRates", locationId, selected?.cycleId, selected?.payPeriodId],
		queryFn: async () => getPayRate(locationId, selected?.cycleId, selected?.payPeriodId),
		enabled: or(!!managedPositions, !!locationId, !!selected),
		staleTime: Infinity,
	});

	const { data: workedHours, isLoading: isLoadingWorkedHours } = useQuery({
		queryKey: ["workedHours", locationId, selected?.cycleId, selected?.payPeriodId],
		queryFn: async () => getWorkedHours(locationId, selected?.cycleId, selected?.payPeriodId),
		enabled: or(!!managedPositions, !!locationId, !!selected),
		staleTime: Infinity,
	});

	const { mutate: ingestData } = useMutation({
		mutationFn: async () => {
			const areValid = or(!!locationId, !!selected, !!selected?.payPeriodId);
			if (!areValid) {
				return false;
			}
			setIngesting(true);
			await requestWorkedHoursIngestion(locationId, selected?.cycleId, selected?.payPeriodId);
			await payRateIngestion(locationId, selected?.cycleId, selected?.payPeriodId);
			return true;
		},
	});

	const isPartialLoading = or(isLoadingPositions, isLoadingPayRates, isLoadingWorkedHours);
	const isLoading = or(ingesting, isPartialLoading);
	const isPresent = or(Boolean(workedHours?.usersWorkedHours?.length), Boolean(payRates?.payRates?.length));

	return (
		<Grid container position={"relative"} alignContent={"flex-start"} sx={{ minHeight: "480px" }}>
			<Grid container item alignContent={"space-between"} sx={{ height: "100%" }}>
				{isAccessGranted && (
					<Grid item xs={12} mb={"50px"}>
						<PayRatesContent
							isDataPresent={isPresent}
							loading={isLoading}
							data={
								isPresent
									? matchDepartments(managedPositions, workedHours?.usersWorkedHours ?? [], payRates?.payRates ?? [])
									: []
							}
						/>
					</Grid>
				)}

				{(!isPresent || !isAccessGranted) && !isLoading && (
					<EmptyBody
						image={selected ? emptyTable : empty}
						title={getEmptyTitle(selected, isAccessGranted)}
						bodyHeight={"480px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				)}

				<RenderButton
					isPartialLoading={isPartialLoading}
					isUpdatable={isUpdatable}
					ingestData={ingestData}
					workedHours={workedHours}
					payRates={payRates}
					isAccessGranted={isAccessGranted}
					SYNC_PAY_RATES={SYNC_PAY_RATES}
					SYNC_TIME_AND_ATTENDANCE={SYNC_TIME_AND_ATTENDANCE}
				/>
			</Grid>
		</Grid>
	);
};

export default PayRatesBody;

interface IRenderButtonProps {
	isPartialLoading: boolean;
	isUpdatable: boolean;
	ingestData: () => void;
	workedHours: Nullable<IPayPeriodWordedHours>;
	payRates: Nullable<IPayPeriodPayRate>;
	isAccessGranted: boolean;
	SYNC_PAY_RATES: boolean;
	SYNC_TIME_AND_ATTENDANCE: boolean;
}

const RenderButton = (props: IRenderButtonProps) => {
	const { isPartialLoading, isUpdatable, ingestData, workedHours, payRates, isAccessGranted, SYNC_PAY_RATES, SYNC_TIME_AND_ATTENDANCE } =
		props;
	const timezone = useSelector((state: RootState) => state.location.location?.timezone);

	const shouldDisplayContent = or(Boolean(workedHours?.usersWorkedHours?.length), Boolean(payRates?.payRates?.length));
	return (
		<Grid item xs={12} mb={2.2} className="time-rate-bottomGrid">
			<Stack direction="row" alignItems={"center"}>
				<TextIconMessage
					icon
					title={"Last Update"}
					content={getLastUpdatedDate(
						workedHours ? workedHours.updatedAt : payRates?.lastModifiedOn,
						timezone ?? "",
						DATE_FORMAT.usDateTime12hUnpadded
					)}
					showContent={shouldDisplayContent}
					loading={isPartialLoading}
					sx={{
						marginRight: isPartialLoading || payRates || workedHours ? "10px" : 0,
						color: "var(--text-color)",
					}}
				/>

				{isUpdatable && (
					<CountDownButton
						disabled={!(SYNC_PAY_RATES && SYNC_TIME_AND_ATTENDANCE) || !isAccessGranted}
						typographySx={{ fontSize: "12px" }}
						initialCopy={"Update Now"}
						actionCopy={"Updating..."}
						loading={isPartialLoading}
						counter={60}
						onClickCallBack={() => ingestData()}
					/>
				)}
			</Stack>
		</Grid>
	);
};
